import {
  Col,
  FileUploadPattern,
  FormRadioGroup,
  Heading,
  Modal,
  Row,
  SectionPage,
  UploadedItem,
} from '@snsw/react-component-library';
import ErrorSummary from '@snsw/react-component-library/build/Components/ErrorSummary/ErrorSummary';
import { PasswordlessContinueSaveExitButtons } from 'shared/components/formNavigationButtons/PasswordlessContinueSaveExitButtons';
import { TopPageSummary } from 'shared/components/topPageSummary/TopPageSummary';
import { PagePath } from 'shared/constants/pagePath';
import { PlantDesignDrawingAndTechDetailsPage } from 'plantDesign/constants/plantDesignDrawingAndTechDetailsPage';
import {
  removeEngineeringPrinciplesFile,
  storeEngineeringPrinciples,
  storeIsCompleted,
} from 'plantDesign/redux/plantDesignDrawingAndTechSlice';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { saveApplication } from 'shared/redux/applicationSlice';
import { radioOptionsYesNo } from 'shared/constants/constants';
import { getAttachmentApi } from 'shared/utils/Helpers';
import ScrollToTop from 'shared/utils/scrollToTop';
import { isEmpty, validate, validateExt } from 'shared/utils/Validation';
import { DrawingListView } from './components/DrawingListView';
import { TechStandardListView } from './components/TechStandardListView';
import styles from './DrawingsAndTechDetails.module.scss';
import { PlantDesignConstants } from 'plantDesign/constants/plantDesignConstants';

export const DrawingsAndTechDetails = (props) => {
  ScrollToTop();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const plantDesign = useSelector((state) => state.plantDesign);
  const plantDesignDrawingAndTech = useSelector((state) => state.plantDesignDrawingAndTech);
  const application = useSelector((state) => state.application);

  const { drawings, techStandards, engineeringPrinciples } = plantDesignDrawingAndTech;

  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [errorSummary, setErrorSummary] = useState([]);
  const [engineeringPrincipleFile, setEngineeringPrincipleFile] = useState(
    engineeringPrinciples?.engineeringPrincipleFile || [],
  );
  const [complyWithTechStandard, setComplyWithTechStandard] = useState(
    engineeringPrinciples?.complyWithTechStandard || '',
  );
  const [showDeleteEngineeringFileModal, setShowDeleteEngineeringModal] = useState(false);

  const summmaryTitlePropertyMap = [
    {
      title: 'Plant design details',
      itemName: 'plantDesign',
      link: PagePath.plantDesignDetails,
    },
  ];

  const PageConstantSummary = PlantDesignDrawingAndTechDetailsPage.summary;
  const PageConstantEngineeringPrinciples = PlantDesignDrawingAndTechDetailsPage.engineeringPrinciples;

  const detailLabelPropertyMap = [
    { title: PageConstantSummary.plantTypeLabel, itemPropertyName: 'plantTypeDescription' },
    { title: PageConstantSummary.subTypeLabel, itemPropertyName: 'subTypeDescription' },
    { title: PageConstantSummary.manufacturerLabel, itemPropertyName: 'manufacturer' },
    { title: PageConstantSummary.modelLabel, itemPropertyName: 'modelNameOrNumber' },
    { title: PageConstantSummary.yearOfDesignLabel, itemPropertyName: 'yearOfManufacture' },
  ];

  const onContinueClick = () => {
    const isValid = validateExt(validators, setErrors, setErrorSummary);

    setShowErrors(!isValid);

    if (isValid) {
      storeDetails(true);

      dispatch(saveApplication(true))
        .unwrap()
        .then(() => {
          navigate(PagePath.plantDesignDesignerVerifierDetails);
        })
        .catch(() => {});
    }
  };

  const storeDetails = (isCompleted) => {
    dispatch(storeIsCompleted(isCompleted));
    let file = [];
    if (complyWithTechStandard === 'no') file = engineeringPrincipleFile;

    dispatch(storeEngineeringPrinciples({ complyWithTechStandard, engineeringPrincipleFile: file }));
  };

  const onPreSaving = () => {
    storeDetails(validate(validators, setErrors));
  };

  const onBackClick = () => {
    storeDetails(validate(validators, setErrors));
    navigate(PagePath.plantDesignDetails);
  };

  const validateDrawings = (drawingList) => {
    return drawingList.reduce((p, c) => {
      return (
        p &&
        !isEmpty(c?.title) &&
        !isEmpty(c?.number) &&
        Array.isArray(c?.uploadedDocument) &&
        !!c?.uploadedDocument?.length
      );
    }, true);
  };

  const validateTechStandards = (techStandardList) => {
    return techStandardList.reduce((p, c) => {
      return p && !isEmpty(c?.yearOfPublication) && !isEmpty(c?.number);
    }, true);
  };

  const validators = [
    {
      fieldName: 'drawingList',
      fieldValue: drawings,
      isValid: (v) => v.length > 0 && validateDrawings(v),
      errorMessage: (v) => (v.length < 1 ? 'You must add at least 1 drawing' : 'Missing mandatory field in drawing'),
      errorSummaryMessage: 'Drawing details',
    },
    {
      fieldName: 'techDetailsList',
      fieldValue: techStandards,
      isValid: (v) => drawings.length == 0 || (v.length > 0 && validateTechStandards(v)),
      errorMessage: (v) =>
        v.length < 1 ? 'You must add at least 1 technical standard' : 'Missing mandatory field in technical standard',
      errorSummaryMessage: 'Technical standard details',
    },
    {
      fieldName: 'complyWithTechStandard',
      fieldValue: complyWithTechStandard,
      isValid: (v) => drawings.length == 0 || techStandards.length == 0 || !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'engineeringPrincipleFile',
      fieldValue: engineeringPrincipleFile,
      isValid: (v) =>
        drawings.length == 0 ||
        v.length > 0 ||
        complyWithTechStandard === 'yes' ||
        isEmpty(complyWithTechStandard) ||
        engineeringPrincipleFile?.length > 0,
      errorMessage: () => 'Please upload a document',
    },
  ];

  const attachmentApi = getAttachmentApi(application.applicationId);

  const fileIdRef = useRef();
  const onRemoveEngineeringPrincipleFile = (fileId) => {
    fileIdRef.current = fileId;
    setShowDeleteEngineeringModal(true);
  };

  const confirmRemoveEngineeringPrincipleFile = async () => {
    if (fileIdRef.current) {
      const resp = await attachmentApi.removeFile(fileIdRef.current);
      if (resp) {
        dispatch(removeEngineeringPrinciplesFile(fileIdRef.current));
        const newFiles = engineeringPrincipleFile.filter((e) => e.id !== fileIdRef.current);
        setEngineeringPrincipleFile(newFiles);
      }
      setShowDeleteEngineeringModal(false);
    }
  };

  const cancelRemoveEngineeringPrincipleFile = () => {
    setShowDeleteEngineeringModal(false);
  };

  useEffect(() => {
    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [complyWithTechStandard, engineeringPrincipleFile]);

  return (
    <>
      <Row>
        <Col span={8}>
          <ErrorSummary errors={errorSummary} singularTitle="We need more information" />
        </Col>
      </Row>
      <SectionPage>
        <TopPageSummary
          titlePropertyMap={summmaryTitlePropertyMap}
          detailPropertyMap={detailLabelPropertyMap}
          item={{ plantDesign }}
        />
      </SectionPage>

      <Row>
        <Col span={8}>
          <DrawingListView
            id="drawingList"
            items={drawings}
            errorMessage={errors?.drawingList?.errorMessage}
            hasError={showErrors && errors?.drawingList?.invalid}
            applicationId={application.applicationId}
          />

          {drawings.length > 0 && (
            <TechStandardListView
              items={techStandards}
              id="techDetailsList"
              hasError={showErrors && errors?.techDetailsList?.invalid}
              errorMessage={errors?.techDetailsList?.errorMessage}
            />
          )}

          {drawings.length > 0 && techStandards.length > 0 && (
            <>
              <FormRadioGroup
                id="complyWithTechStandard"
                name="complyWithTechStandard"
                options={radioOptionsYesNo}
                onChange={setComplyWithTechStandard}
                value={complyWithTechStandard}
                legend={PageConstantEngineeringPrinciples.designComplianceLabel}
                errorMessage={errors?.complyWithTechStandard?.errorMessage}
                hasError={showErrors && errors?.complyWithTechStandard?.invalid}
                margin={{ top: 'xxxl' }}
              />
              {complyWithTechStandard === 'no' && (
                <>
                  <Heading level={3} style={{ marginTop: '3.5rem', marginBottom: '0.313rem' }}>
                    {PageConstantEngineeringPrinciples.uploadFileHeader}:
                  </Heading>
                  <span className={styles.labelMsg}>{PageConstantEngineeringPrinciples.uploadFileDescription}</span>
                  {(!engineeringPrincipleFile || engineeringPrincipleFile.length == 0) && (
                    <>
                      <Heading level={4} style={{ marginTop: '1rem', marginBottom: 0 }}>
                        Document requirements:
                      </Heading>
                      <ol className={styles.listItem}>
                        <li>Documents must be in English.</li>
                        <li>
                          You can only submit electronic documents. If you are uploading a scanned copy of a print
                          document, check the document image is clear and all text is legible.
                        </li>
                      </ol>
                    </>
                  )}

                  {(!engineeringPrincipleFile || engineeringPrincipleFile.length < 3) && (
                    <div className={styles.marginTopTwoHalfRem + ' ' + styles.engineeringFile}>
                      <FileUploadPattern
                        api={attachmentApi}
                        name="engineeringPrincipleFile"
                        id="engineeringPrincipleFile"
                        legendText=""
                        initialFiles={engineeringPrincipleFile}
                        onUploaded={setEngineeringPrincipleFile}
                        onRemoved={setEngineeringPrincipleFile}
                        maxMBSize={10}
                        maxFiles={3}
                        acceptedFormats=".png,.jpg,.pdf"
                        errorMessage={errors?.engineeringPrincipleFile?.errorMessage}
                      />
                    </div>
                  )}

                  {engineeringPrincipleFile && engineeringPrincipleFile.length > 0 && (
                    <Heading level={4} style={{ marginBottom: '1rem', marginTop: '2rem' }}>
                      {PageConstantEngineeringPrinciples.uploadedFileLabel}:
                    </Heading>
                  )}
                  {engineeringPrincipleFile.map((e) => (
                    <UploadedItem {...e} onRemove={() => onRemoveEngineeringPrincipleFile(e.id)} key={e.id} />
                  ))}
                </>
              )}
            </>
          )}

          {showDeleteEngineeringFileModal && (
            <Modal
              title="Are you sure you want to remove this engineering principles?"
              description="This will delete the file."
              buttons={[
                { text: 'Remove', onClick: confirmRemoveEngineeringPrincipleFile },
                { text: 'Cancel', onClick: cancelRemoveEngineeringPrincipleFile },
              ]}
            />
          )}
        </Col>
      </Row>

      <PasswordlessContinueSaveExitButtons
        onBackClick={onBackClick}
        onContinueClick={onContinueClick}
        onPreSaving={onPreSaving}
        licenceType={PlantDesignConstants.licenceType}
      />
    </>
  );
};
